import type { CustomField, FieldDataType } from '@feathr/blackbox';

import defaultPersonFields from '@feathr/blackbox/fixtures/defaultPersonFields.json';

/** Initial-selected Person field IDs — match the legacy SegmentExportModal selection. */
export const DEFAULT_PERSON_FIELD_IDS: readonly string[] = ['name', 'email'];

export interface IPersonFieldOption {
  id: string;
  name: string;
  type: FieldDataType;
}

/**
 * Resolves the export-name template.
 *
 * `{{ group-name }}` → the segment's name (falls back to `group` when empty).
 * `{{ date }}` → today formatted as `YYYY-MM-DD` (UTC).
 *
 * @param groupName - The segment's name.
 * @param today - Optional date to use for the date token. Defaults to `new Date()`.
 * @returns The resolved export name.
 *
 * @example
 *   resolveExportNameTemplate('Annual-Donors', new Date('2026-05-05')) // 'Annual-Donors_2026-05-05'
 */
export function resolveExportNameTemplate(groupName: string, today: Date = new Date()): string {
  const safeName = groupName.trim().length > 0 ? groupName : 'group';
  const datePart = today.toISOString().split('T')[0];
  return `${safeName}_${datePart}`;
}

/**
 * Merges the default Person fields with custom Person fields from the account.
 * Custom fields get the `custom.<f_key>` id prefix per the existing export contract.
 *
 * @param customFields - Custom field models (already filtered to `FieldCollection.Person`).
 * @returns Combined list of selectable Person field options.
 */
export function getPersonFieldOptions(customFields: CustomField[]): IPersonFieldOption[] {
  const defaults: IPersonFieldOption[] = defaultPersonFields.map((f) => ({
    id: f.id,
    name: f.name,
    type: f.type as FieldDataType,
  }));

  const customOptions: IPersonFieldOption[] = customFields.map((field) => ({
    id: `custom.${field.get('f_key')}`,
    name: field.get('u_key'),
    type: field.get('data_type'),
  }));

  return [...defaults, ...customOptions];
}
